import z from 'zod';
/** At most this many facts per envelope; the builder keeps the first ones in snapshot order. */
export const COMPLIANCE_JOB_CARD_FACTS_CAP = 5_000;
/** At most this many references per fact; the builder keeps the first ones. */
export const COMPLIANCE_JOB_CARD_REFERENCES_CAP = 50;
/**
 * At most this many part numbers and, separately, this many serial numbers per fact; the builder
 * keeps the first ones. The Oil writer rejects the whole envelope above this cap (DR-5468), so
 * Brain enforces it first.
 */
export const COMPLIANCE_JOB_CARD_PART_IDENTITIES_CAP = 50;
/** Every string field is at most this many characters; the builder truncates longer values. */
export const COMPLIANCE_JOB_CARD_STRING_CAP = 200;
const cappedString = z.string().max(COMPLIANCE_JOB_CARD_STRING_CAP);
/**
 * A project's compliance job-card facts (DR-5456): the AD/SB references that on-wing job cards,
 * task cards, non-routine cards, tech log pages and work orders state, with the card identity and
 * its sign-off.
 *
 * One snapshot per project per finalize. It replaces the previous snapshot for the project. There
 * is one fact per source page; a page with no AD/SB reference yields no fact. A project that held
 * facts and lost them, or whose cards hold results but no fact, sends an EMPTY snapshot (`facts:
 * []`): the consumer sets the project's active facts inactive and records the snapshot time
 * (DR-5453 spec BP-21), which is how lost facts retract. A project that never held a result sends
 * nothing.
 *
 * `outcome` is what the card states for a reference: `complied` (accomplished), `previously-
 * complied` (PCW), `not-applicable`, `not-complied` (ND / NCW / open / deferred) or `planned` ("to
 * be performed", a future instruction); `null` when the card states none. v2 rule (DR-5453 spec,
 * Clarified row, Dzhamal #devsync 2026-10-19): `complied` is a proof candidate, and the sign-off
 * guard still applies; `previously-complied`, `not-applicable`, `not-complied`, `planned` and
 * `null` set the candidate aside with the outcome named, verdict "Check proof". A signed N/A
 * marking is not an accomplishment and does not substantiate N/A in v2 (a later scoping item).
 * Records note (Rinoj, #testing-brain 2026-10-19): a stamped PCW is a pointer only, with no
 * accomplishment date, so the original card or shop record is the proof to ask for.
 *
 * `signedOff` (page-level and per-line): `false` and `null` both mean "not certified" for the
 * consumer (Rinoj ruling 2026-10-19). A list page (more than one reference) proves a line only when
 * that line's own `signedOff` is true.
 *
 * `partNumbers` / `serialNumbers` (DR-5488, DR-5453 DT-43): the part and serial numbers the card
 * prints for the components it names. The v2 part guard sets a part-level AD or SB row aside as
 * `on-wing-part-unverified` when the card names the AD but neither list carries the row's part
 * number or serial; v2 compares upper-case letters and digits only, so Brain emits the printed
 * form. Both lists are required: a card without component identities sends empty lists. One fact
 * holds every reference and every part of the page, so a part printed under one task can match
 * another reference on the same page (page scope, as the references).
 *
 * `generatedAt` is REQUIRED on this family (Dzhamal 2026-10-19): the Oil writer skips an envelope
 * whose `generatedAt` is older than or equal to the last applied one as stale, so an unstamped
 * snapshot has no defined fate. Brain stamps it with the finalization write's own `finalizedAt`,
 * not the emit time, so the stamp follows commit order across two overlapping finalizes of one
 * project. The stale rule stays on `generatedAt`. `generation` (DR-5896, optional) names the finalize
 * generation the envelope was built from, so a consumer can tie it to forecast-source's
 * `finalizationGeneration`, as `engine-movement-evidence-ready`.
 *
 * Rollout: Brain sends this family only while `COMPLIANCE_JOB_CARD_FACTS_READY_ENABLED=true` (off
 * by default), because Oil rejects an unknown `type`.
 */
export const complianceJobCardFactsReadyWebhookSchema = z.looseObject({
    type: z.literal('compliance-job-card-facts-ready'),
    projectId: z.string(),
    /** The finalization write's stamp; the consumer's per-family staleness watermark (DR-3278). Required on this family. */
    generatedAt: z.iso.datetime(),
    /** The finalize's webhook generation the envelope was built from (DR-5896). Optional; not the stale rule. */
    generation: z.number().int().positive().max(2_147_483_647).optional(),
    facts: z
        .array(z.looseObject({
        references: z
            .array(z.looseObject({
            kind: z.enum(['ad', 'sb']),
            number: cappedString,
            baseNumber: cappedString,
            revision: cappedString.nullable(),
            signedOff: z.boolean().nullable(),
            outcome: z
                .enum(['complied', 'previously-complied', 'not-applicable', 'not-complied', 'planned'])
                .nullable(),
        }))
            .max(COMPLIANCE_JOB_CARD_REFERENCES_CAP),
        cardNumber: cappedString.nullable(),
        workOrder: cappedString.nullable(),
        taskReference: cappedString.nullable(),
        ataReference: cappedString.nullable(),
        /** ISO YYYY-MM-DD when readable. Not `z.iso.date()`: a stored value is passed through as read. */
        cardDate: cappedString.nullable(),
        engineSerial: cappedString.nullable(),
        aircraftRegistration: cappedString.nullable(),
        signedOff: z.boolean().nullable(),
        /** Part numbers the card prints for the components it names, as printed, deduplicated (DR-5488). */
        partNumbers: z.array(cappedString).max(COMPLIANCE_JOB_CARD_PART_IDENTITIES_CAP),
        /** Serial numbers of those components, as printed, deduplicated (DR-5488). */
        serialNumbers: z.array(cappedString).max(COMPLIANCE_JOB_CARD_PART_IDENTITIES_CAP),
        /** Representative live document of the reconstructed entity. */
        documentId: z.string(),
        /** Stable id of the reconstructed entity. */
        reconstructedDocumentId: z.string(),
        /** The page the fact was read from — the evidence deep-link. */
        evidencePageId: z.string(),
    }))
        .max(COMPLIANCE_JOB_CARD_FACTS_CAP),
});
//# sourceMappingURL=compliance-job-card-facts-ready.webhook.js.map